import { useEffect, useState, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import StatusBadge from '../../components/StatusBadge'
import { Button } from '../../components/FormControls'
import { ErrorBanner, SuccessBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

const NEXT_STATUS = {
  draft: 'under_review',
  under_review: 'manager_approved',
  manager_approved: 'committee_approved',
}
const STATUS_ACTION_LABEL = {
  draft: 'Submit for Review',
  under_review: 'Approve (Manager)',
  manager_approved: 'Approve (Committee)',
}

export default function LifecyclePage() {
  const { tenderId, revisionId } = useParams()
  const navigate = useNavigate()
  const [revision, setRevision] = useState(null)
  const [log, setLog] = useState(null)
  const [freezeSnapshot, setFreezeSnapshot] = useState(undefined)
  const [exports, setExports] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [busy, setBusy] = useState(false)
  // Production Export Preview — separate state, separate RPC. This is
  // read-only: build_costspace_export_payload() is the sole source of
  // truth for every number and every ERROR/WARNING/INFO shown here.
  // No calculation or validation logic lives in this component.
  const [exportPreview, setExportPreview] = useState(null) // null = not generated yet
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false)
  const [exportPreviewError, setExportPreviewError] = useState(null)
  const [payloadExpanded, setPayloadExpanded] = useState(false)
  const [expandedActivities, setExpandedActivities] = useState(new Set())
  const [expandedIndirect, setExpandedIndirect] = useState(new Set())

  const loadAll = useCallback(async () => {
    const [revRes, logRes] = await Promise.all([
      supabase.from('estimate_revision').select('id, label, status').eq('id', revisionId).single(),
      supabase.from('approval_log').select('from_status, to_status, note, occurred_at').eq('estimate_revision_id', revisionId).order('occurred_at'),
    ])
    if (revRes.error) return setError(revRes.error.message)
    if (logRes.error) return setError(logRes.error.message)
    setRevision(revRes.data)
    setLog(logRes.data)

    const { data: snap } = await supabase
      .from('freeze_snapshot')
      .select('id, tender_price_total, direct_cost_total, frozen_at, frozen_by')
      .eq('estimate_revision_id', revisionId)
      .maybeSingle()
    setFreezeSnapshot(snap)

    if (snap) {
      const { data: exp } = await supabase
        .from('export_package')
        .select('id, exported_at, format_version')
        .eq('freeze_snapshot_id', snap.id)
        .order('exported_at', { ascending: false })
      setExports(exp || [])
    } else {
      setExports([])
    }
  }, [revisionId])

  useEffect(() => { loadAll() }, [loadAll])

  async function handleAdvance() {
    const next = NEXT_STATUS[revision.status]
    if (!next) return
    setBusy(true)
    setError(null)
    setSuccessMsg(null)
    const { error } = await supabase.from('estimate_revision').update({ status: next }).eq('id', revisionId)
    if (error) setError(error.message)
    else { setSuccessMsg(`Advanced to ${next.replace(/_/g, ' ')}.`); await loadAll() }
    setBusy(false)
  }

  async function handleRollback() {
    setBusy(true)
    setError(null)
    setSuccessMsg(null)
    const { error } = await supabase.rpc('rollback_revision_to_draft', {
      p_revision_id: revisionId,
      p_reason: 'Manual rollback requested from Lifecycle page',
    })
    if (error) setError(error.message)
    else { setSuccessMsg('Rolled back to draft.'); await loadAll() }
    setBusy(false)
  }

  async function handleUnfreeze() {
    const confirmed = window.confirm(
      'Unfreeze this revision?\n\n' +
      '• The revision will return to Draft status.\n' +
      '• The active freeze snapshot will be removed from the active snapshot table.\n' +
      '• The frozen state remains reviewable through the existing audit trail if needed.'
    )
    if (!confirmed) return
    setBusy(true)
    setError(null)
    setSuccessMsg(null)
    const { error } = await supabase.rpc('unfreeze_estimate_revision', {
      p_revision_id: revisionId,
      p_reason: 'Manual unfreeze requested from Lifecycle page',
    })
    if (error) setError(error.message)
    else { setSuccessMsg('Revision unfrozen. Returned to draft.'); await loadAll() }
    setBusy(false)
  }

  async function handleFreeze() {
    setBusy(true)
    setError(null)
    setSuccessMsg(null)
    const { data: authData } = await supabase.auth.getUser()
    const { error } = await supabase.rpc('freeze_estimate_revision', {
      p_revision_id: revisionId,
      p_frozen_by: authData.user?.id,
    })
    if (error) setError(error.message)
    else { setSuccessMsg('Revision frozen. Snapshot created.'); await loadAll() }
    setBusy(false)
  }

  // Production Export Preview — calls the approved RPC and renders its
  // response verbatim. Deliberately separate from handleExport() above,
  // which remains the existing Lifecycle Export path, untouched.
  async function handleGenerateExportPreview() {
    setExportPreviewLoading(true)
    setExportPreviewError(null)
    const { data, error } = await supabase.rpc('build_costspace_export_payload', {
      p_freeze_snapshot_id: freezeSnapshot.id,
    })
    if (error) setExportPreviewError(error.message)
    else setExportPreview(data)
    setExportPreviewLoading(false)
  }

  async function handleExport() {
    setBusy(true)
    setError(null)
    setSuccessMsg(null)
    const { data: authData } = await supabase.auth.getUser()

    const { data: fullSnapshot, error: snapErr } = await supabase
      .from('freeze_snapshot')
      .select('*')
      .eq('id', freezeSnapshot.id)
      .single()

    if (snapErr) { setError(snapErr.message); setBusy(false); return }

    const { error } = await supabase.from('export_package').insert({
      freeze_snapshot_id: freezeSnapshot.id,
      exported_by: authData.user?.id,
      format_version: 'v1',
      payload: fullSnapshot,
    })
    if (error) setError(error.message)
    else { setSuccessMsg('Export package created.'); await loadAll() }
    setBusy(false)
  }

  if (error && !revision) return <ErrorBanner message={error} />
  if (!revision) return <SkeletonRows rows={4} cols={2} />

  const isFrozen = revision.status === 'frozen'
  const canAdvance = !!NEXT_STATUS[revision.status]
  const canRollback = ['under_review', 'manager_approved', 'committee_approved'].includes(revision.status)
  const canFreeze = revision.status === 'committee_approved'

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <EstimationBreadcrumb />
        {/* Minimal exit point back to CostSpace, added as part of the
            first integration slice. Uses the single global BrowserRouter
            (owned by CostSpace) via useNavigate -- an SPA transition, not
            a full page reload, per the approved architecture. */}
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, color: colors.textSecondary, textDecoration: 'none', cursor: 'pointer' }}>
          Exit to CostSpace &rarr;
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 4px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Approval, Freeze &amp; Export</h1>
        <StatusBadge status={revision.status} />
      </div>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 16px' }}>{revision.label}</p>

      {/* Real production navigation into the Cost Control review layer
          -- available on the current Draft revision (Direct Budget
          Review reads live data pre-freeze) and remains valid after
          freezing (it then reads the immutable snapshot instead). */}
      <div style={{ marginBottom: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Link
          to={`/estimation/tender/${tenderId}/revision/${revisionId}/direct-budget-review`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8,
            color: colors.textPrimary, textDecoration: 'none', fontSize: 13, fontWeight: 600,
          }}
        >
          📋 Direct Budget Review — Cost Control
        </Link>
        <Link
          to={`/estimation/tender/${tenderId}/revision/${revisionId}/indirect-budget-review`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8,
            color: colors.textPrimary, textDecoration: 'none', fontSize: 13, fontWeight: 600,
          }}
        >
          📋 Indirect Budget Review — Cost Control
        </Link>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      <section style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 12px' }}>Approval Workflow</h2>

        {isFrozen ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ padding: '12px 16px', borderRadius: 8, background: '#ECFEFF', color: '#0E7490', fontSize: 14, fontWeight: 600 }}>
              🔒 This revision is frozen. No further status changes are possible.
            </div>
            <Button variant="secondary" onClick={handleUnfreeze} disabled={busy}>
              {busy ? 'Working…' : 'Unfreeze Revision'}
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {canAdvance && (
              <Button onClick={handleAdvance} disabled={busy}>
                {busy ? 'Working…' : STATUS_ACTION_LABEL[revision.status]}
              </Button>
            )}
            {canRollback && (
              <Button variant="secondary" onClick={handleRollback} disabled={busy}>
                Rollback to Draft
              </Button>
            )}
            {canFreeze && (
              <Button onClick={handleFreeze} disabled={busy} style={{ background: '#0E7490' }}>
                {busy ? 'Freezing…' : '🔒 Freeze Revision'}
              </Button>
            )}
          </div>
        )}

        <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 10 }}>
          Editing any BOQ item, rate build-up, indirect cost, risk, or markup while under review or approved will
          automatically roll the revision back to draft — this is enforced by the database, not by this screen.
        </p>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 12px' }}>Approval Log</h2>
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {log === null && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={3} /></div>}
          {log?.length === 0 && <div style={{ padding: 20 }}><EmptyState title="No transitions yet" /></div>}
          {log?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>From</Th><Th>To</Th><Th>Note</Th><Th align="right">When</Th>
              </tr></thead>
              <tbody>
                {log.map((l, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Td muted>{l.from_status || '—'}</Td>
                    <Td>{l.to_status}</Td>
                    <Td muted>{l.note || '—'}</Td>
                    <Td align="right" muted>{new Date(l.occurred_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 12px' }}>Freeze Snapshot</h2>
        {freezeSnapshot === undefined && <SkeletonRows rows={1} cols={2} />}
        {freezeSnapshot === null && (
          <div style={{ padding: 20, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8 }}>
            <EmptyState title="Not frozen yet" description="Complete the approval workflow, then freeze the revision to create an immutable snapshot." />
          </div>
        )}
        {freezeSnapshot && (
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', gap: 32, marginBottom: 14 }}>
              <SummaryFigure label="Frozen Tender Price" value={formatNumber(freezeSnapshot.tender_price_total)} />
              <SummaryFigure label="Frozen Direct Cost" value={formatNumber(freezeSnapshot.direct_cost_total)} />
              <SummaryFigure label="Frozen At" value={new Date(freezeSnapshot.frozen_at).toLocaleString()} />
            </div>
            <Link to={`/estimation/tender/${tenderId}/revision/${revisionId}/budget-summary`}
              style={{ display: 'inline-block', padding: '8px 16px', background: '#0F1017', color: '#FFF', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              📊 View Budget Summary
            </Link>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* PRODUCTION EXPORT PREVIEW — reads build_costspace_export_
          payload() verbatim. No calculation, no validation, no derived
          business status in this component: errors.length > 0 is a
          direct read of the RPC's own array, not a new rule. */}
      {/* ============================================================ */}
      {isFrozen && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 12px' }}>Production Export Preview (CostSpace)</h2>

          <div style={{ marginBottom: 12 }}>
            <Button onClick={handleGenerateExportPreview} disabled={exportPreviewLoading} variant="secondary">
              {exportPreviewLoading ? 'Generating…' : (exportPreview ? 'Regenerate Preview' : 'Generate Preview')}
            </Button>
          </div>

          {exportPreviewError && <ErrorBanner message={exportPreviewError} />}

          {exportPreviewLoading && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={3} /></div>}

          {!exportPreviewLoading && exportPreview === null && !exportPreviewError && (
            <div style={{ padding: 20, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8 }}>
              <EmptyState title="No preview generated" description="Generate a preview to validate this frozen revision against the CostSpace Direct/Indirect Budget contract." />
            </div>
          )}

          {exportPreview && (() => {
            const errs = exportPreview.validation?.errors || []
            const warns = exportPreview.validation?.warnings || []
            const infos = exportPreview.validation?.info || []
            const blocked = errs.length > 0
            const directBudget = exportPreview.direct_budget || []
            const indirectBudget = exportPreview.indirect_budget || []
            // SSOT: every number below is read verbatim from the RPC
            // response. This total is a display-only SUM over already-
            // computed values — not a recalculation of any cost.
            const directTotal = directBudget.reduce((s, r) => s + (r.activity?.total_budget || 0), 0)
            const indirectTotal = indirectBudget.reduce((s, r) => s + (r.tender_budget || 0), 0)

            function toggleActivity(itemNo) {
              setExpandedActivities((prev) => {
                const next = new Set(prev)
                next.has(itemNo) ? next.delete(itemNo) : next.add(itemNo)
                return next
              })
            }
            function toggleIndirect(idx) {
              setExpandedIndirect((prev) => {
                const next = new Set(prev)
                next.has(idx) ? next.delete(idx) : next.add(idx)
                return next
              })
            }

            return (
              <>
                <div style={{
                  padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 600,
                  background: blocked ? '#FEE2E2' : '#D1FAE5', color: blocked ? '#991B1B' : '#065F46',
                }}>
                  {blocked ? `🚫 Export Blocked — ${errs.length} error${errs.length !== 1 ? 's' : ''} must be resolved` : '✅ Ready to Export'}
                </div>

                <div style={{ display: 'flex', gap: 32, marginBottom: 20, flexWrap: 'wrap' }}>
                  <SummaryFigure label="Direct Budget Total" value={formatNumber(directTotal)} />
                  <SummaryFigure label="Indirect Budget Total" value={formatNumber(indirectTotal)} />
                  <SummaryFigure label="Errors" value={String(errs.length)} />
                  <SummaryFigure label="Warnings" value={String(warns.length)} />
                </div>

                {(errs.length > 0 || warns.length > 0 || infos.length > 0) && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, margin: '0 0 8px' }}>Validation</div>
                    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                          <Th>Level</Th><Th>Item</Th><Th>Message</Th>
                        </tr></thead>
                        <tbody>
                          {errs.map((v, i) => (
                            <tr key={`e${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <Td><span style={{ color: '#991B1B', fontWeight: 700 }}>ERROR</span></Td>
                              <Td muted>{v.item_no || '—'}</Td><Td>{v.message}</Td>
                            </tr>
                          ))}
                          {warns.map((v, i) => (
                            <tr key={`w${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <Td><span style={{ color: '#92400E', fontWeight: 700 }}>WARNING</span></Td>
                              <Td muted>{v.item_no || '—'}</Td><Td>{v.message}</Td>
                            </tr>
                          ))}
                          {infos.map((v, i) => (
                            <tr key={`i${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <Td><span style={{ color: colors.textMuted, fontWeight: 700 }}>INFO</span></Td>
                              <Td muted>{v.item_no || '—'}</Td><Td>{v.message}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* DIRECT BUDGET — Activity rows, each expandable to its
                    Resource rows. Structure preserved exactly as the v3
                    payload returns it: no flattening, no aggregation. */}
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, margin: '0 0 10px' }}>Direct Budget</div>
                <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'auto', marginBottom: 24 }}>
                  {directBudget.length === 0 ? (
                    <div style={{ padding: 20 }}><EmptyState title="No exportable Direct Budget rows" /></div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
                      <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                        <Th></Th><Th>Item No.</Th><Th>Description</Th><Th align="right">Qty</Th><Th>Unit</Th><Th>Cost Code</Th><Th align="right">Total Budget</Th>
                      </tr></thead>
                      <tbody>
                        {directBudget.map((row, i) => {
                          const isOpen = expandedActivities.has(row.item_number)
                          return (
                            <>
                              <tr key={`a${i}`} style={{ borderBottom: `1px solid ${colors.border}`, cursor: row.resources?.length ? 'pointer' : 'default' }}
                                  onClick={() => row.resources?.length && toggleActivity(row.item_number)}>
                                <Td>{row.resources?.length > 0 ? (isOpen ? '▾' : '▸') : ''}</Td>
                                <Td>{row.item_number}</Td>
                                <Td muted>{row.activity?.description}</Td>
                                <Td align="right">{formatNumber(row.activity?.qty)}</Td>
                                <Td muted>{row.activity?.unit}</Td>
                                <Td muted>{row.activity?.cost_code || '—'}</Td>
                                <Td align="right">{formatNumber(row.activity?.total_budget)}</Td>
                              </tr>
                              {isOpen && (row.resources || []).map((res, j) => (
                                <tr key={`a${i}r${j}`} style={{ borderBottom: `1px solid ${colors.border}`, background: '#FAFBFC' }}>
                                  <Td></Td>
                                  <Td colSpan={1}></Td>
                                  <Td muted style={{ paddingLeft: 24 }}>
                                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{res.row_type || '—'}</span>
                                    {res.resource_code ? ` · ${res.resource_code}` : ''}
                                    {res.resource_description ? ` — ${res.resource_description}` : ''}
                                  </Td>
                                  <Td align="right">{formatNumber(res.qty)}</Td>
                                  <Td muted>{res.unit || '—'}</Td>
                                  <Td muted>{formatNumber(res.unit_rate)}</Td>
                                  <Td align="right">{formatNumber(res.total)}</Td>
                                </tr>
                              ))}
                            </>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* INDIRECT BUDGET — Cost Code/Department intentionally
                    absent per the finalized ownership model. Each row
                    expands to its Planned Monthly Distribution. */}
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, margin: '0 0 10px' }}>Indirect Budget</div>
                <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'auto', marginBottom: 24 }}>
                  {indirectBudget.length === 0 ? (
                    <div style={{ padding: 20 }}><EmptyState title="No Indirect Budget rows" /></div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
                      <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                        <Th></Th><Th>Description</Th><Th align="right">Qty</Th><Th>Unit</Th><Th align="right">Indirect Budget Rate</Th><Th align="right">Tender Budget</Th>
                      </tr></thead>
                      <tbody>
                        {indirectBudget.map((row, i) => {
                          const isOpen = expandedIndirect.has(i)
                          const dist = row.planned_monthly_distribution || {}
                          const months = Object.keys(dist)
                          return (
                            <>
                              <tr key={`i${i}`} style={{ borderBottom: `1px solid ${colors.border}`, cursor: months.length ? 'pointer' : 'default' }}
                                  onClick={() => months.length && toggleIndirect(i)}>
                                <Td>{months.length > 0 ? (isOpen ? '▾' : '▸') : ''}</Td>
                                <Td>{row.description}</Td>
                                <Td align="right">{formatNumber(row.qty)}</Td>
                                <Td muted>{row.unit || '—'}</Td>
                                <Td align="right">{formatNumber(row.indirect_budget_rate)}</Td>
                                <Td align="right">{formatNumber(row.tender_budget)}</Td>
                              </tr>
                              {isOpen && (
                                <tr key={`i${i}m`}>
                                  <Td colSpan={6}>
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 0 8px 24px' }}>
                                      {months.map((m) => (
                                        <div key={m} style={{ fontSize: 12 }}>
                                          <span style={{ color: colors.textMuted }}>{m}:</span>{' '}
                                          <span style={{ fontWeight: 600 }}>{formatNumber(dist[m])}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </Td>
                                </tr>
                              )}
                            </>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <button
                  onClick={() => setPayloadExpanded((v) => !v)}
                  style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}
                >
                  {payloadExpanded ? '▾' : '▸'} Raw Export Payload ({exportPreview.export_version})
                </button>
                {payloadExpanded && (
                  <pre style={{
                    background: '#0F172A', color: '#E2E8F0', fontSize: 11, padding: 16, borderRadius: 8,
                    overflow: 'auto', maxHeight: 400, margin: 0,
                  }}>
                    {JSON.stringify(exportPreview, null, 2)}
                  </pre>
                )}
              </>
            )
          })()}
        </section>
      )}

      <section>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 12px' }}>Export Packages</h2>
        {freezeSnapshot && (
          <div style={{ marginBottom: 12 }}>
            <Button onClick={handleExport} disabled={busy}>{busy ? 'Exporting…' : '+ Create Export Package'}</Button>
          </div>
        )}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {exports === null && <div style={{ padding: 16 }}><SkeletonRows rows={1} cols={2} /></div>}
          {exports?.length === 0 && <div style={{ padding: 20 }}><EmptyState title="No exports yet" description={freezeSnapshot ? 'Create the first export package above.' : 'Freeze the revision first.'} /></div>}
          {exports?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>Format</Th><Th align="right">Exported At</Th>
              </tr></thead>
              <tbody>
                {exports.map((e) => (
                  <tr key={e.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Td>{e.format_version}</Td>
                    <Td align="right" muted>{new Date(e.exported_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  )
}

function SummaryFigure({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>{value}</div>
    </div>
  )
}
function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted }) {
  return <td style={{ textAlign: align, padding: '11px 16px', fontSize: 13, color: muted ? colors.textMuted : colors.textPrimary }}>{children}</td>
}
